import { useMemo, useState } from 'react';
import {
  useMyBackdatedHoursRequests,
  useDeleteBackdatedHoursRequest,
} from '../../hooks/useBackdatedHoursRequests';
import { formatDisplayDate } from '../../utils/format';
import { toErrorMessage } from '../../utils/toErrorMessage';
import type { BackdatedHoursRequest } from '../../services/api/backdatedHoursRequests';

/**
 * MKS-265 — the requester's own view of their backdated-hours requests.
 *
 * MKS-208 shipped `GET /api/backdated-hours-requests/my` and
 * `useMyBackdatedHoursRequests`, and then no component ever called them: the
 * employee filed a request and could only find out what happened by noticing
 * whether the hours turned up in the grid. This is the screen that was
 * missing, modelled on the "My Project Join Requests" table already on this
 * page.
 *
 * The decline reason is the point. The approver's decline dialog has always
 * promised "The requester sees the reason you give here" — it is only true
 * from here on.
 */

/**
 * How far back a decided request stays on the list. Pending ones are shown
 * however old they are: an unanswered request is never stale news.
 *
 * The window is applied here rather than on the endpoint deliberately. The
 * route returns only the caller's OWN rows and a person has a handful of them,
 * so a server-side window would buy nothing and would change the route schema,
 * the OpenAPI snapshot and the route-table baseline.
 */
const RECENT_DAYS = 60;

function isRecent(req: BackdatedHoursRequest, now: number): boolean {
  const decidedAt = Date.parse(req.updatedAt);
  if (Number.isNaN(decidedAt)) return true;
  return now - decidedAt <= RECENT_DAYS * 24 * 60 * 60 * 1000;
}

const STATE_LABELS: Record<BackdatedHoursRequest['state'], string> = {
  pending: 'Pending',
  approved: 'Approved',
  declined: 'Declined',
};

const STATE_CLASSES: Record<BackdatedHoursRequest['state'], string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  declined: 'bg-red-100 text-red-800',
};

export function MyBackdatedHoursRequests() {
  const { data: requests } = useMyBackdatedHoursRequests();
  const deleteMutation = useDeleteBackdatedHoursRequest();
  const [withdrawing, setWithdrawing] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const visible = useMemo(() => {
    if (!requests) return [];
    const now = Date.now();
    return requests.filter((req) => req.state === 'pending' || isRecent(req, now));
  }, [requests]);

  if (visible.length === 0) return null;

  const handleWithdraw = async (id: number) => {
    setError(null);
    setWithdrawing(id);
    try {
      await deleteMutation.mutateAsync(id);
    } catch (err) {
      setError(toErrorMessage(err, 'Failed to withdraw the request'));
    } finally {
      setWithdrawing(null);
    }
  };

  return (
    <div className="mt-8">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">My Backdated Hours Requests</h3>
      {error && (
        <div className="mb-3 rounded-md bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-700">
          {error}
        </div>
      )}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hours</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Decided by</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {visible.map((req) => (
              <tr key={req.id} className="hover:bg-gray-50">
                <td className="px-4 py-2 text-sm text-gray-900">{formatDisplayDate(req.date)}</td>
                <td className="px-4 py-2 text-sm font-medium text-gray-900">
                  {req.projectName ?? '-'}
                </td>
                <td className="px-4 py-2 text-sm text-gray-500">{req.hours} h</td>
                <td className="px-4 py-2">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATE_CLASSES[req.state]}`}
                  >
                    {STATE_LABELS[req.state]}
                  </span>
                </td>
                <td className="px-4 py-2 text-sm text-gray-500">
                  {req.state === 'pending' ? '-' : (req.approvedBy?.name ?? '-')}
                </td>
                <td className="px-4 py-2 text-sm text-gray-500">
                  {req.state === 'declined' && (
                    <span className="text-red-600">
                      {req.declineReason
                        ? `Reason: ${req.declineReason}`
                        : 'No reason was given.'}
                    </span>
                  )}
                  {req.state === 'approved' && (
                    <span className="text-green-600">Hours added to your timesheet</span>
                  )}
                </td>
                <td className="px-4 py-2 text-sm">
                  {/* Only a pending request can be withdrawn — an approved one
                      already wrote an entry, and a declined one is closed. The
                      API enforces the same rule; this only keeps the UI from
                      offering a button that always fails. */}
                  {req.state === 'pending' && (
                    <button
                      onClick={() => handleWithdraw(req.id)}
                      disabled={withdrawing === req.id}
                      className="text-red-600 hover:text-red-800 text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {withdrawing === req.id ? 'Withdrawing...' : 'Withdraw'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default MyBackdatedHoursRequests;
